import React, {useMemo} from "react";
import useAuthentication from "src/core/authentication/hooks/useAuthentication";
import ContactInfoFormSection from "./ContactInfoFormSection";
import {Form} from "react-final-form";
import styled from "styled-components";
import {
  FormContainer,
  FormFields,
  FormText,
} from "../../../common/components/collections/form/forms";
import PrimaryLoadingButton from "../../../common/components/elements/button/PrimaryButton";
import Modal from "../../../common/components/modules/modal/Modal";
import GoToLogin from "../../components/GoToLogin";
import DatePicker from "../../../common/components/elements/date-picker/DatePicker";
import {composeValidators, required} from "../../../common/validations";
import useFeatureToggles from "../../../common/hooks/useFeatureToggles";
import {TitleLabel} from "../../../checkout/components/components";
import {yearsAgo, getErrorMessages} from "src/core/common/utils";
import useFormsCache from "src/core/common/hooks/useFormsCache";
import curry from "lodash/curry";
import get from "lodash/get";
import MarketingSourcesSelect from "../../../authentication/components/MarketingSourcesSelect";
import useMarketingSourceParam from "../../../authentication/hooks/useMarketingSourceParam";
import useAgeValidation from "../../../authentication/hooks/useAgeValidation";
import useSite from "../../../sites/hooks/useSite";
import useDeliveryType from "../../../deliveries/hooks/useDeliveryType";
import MarketingOptInField from "../../../authentication/components/MarketingOptInField";
import useRouter from "../../../common/hooks/useRouter";
import useDeliveryAddress from "../../../deliveries/hooks/useDeliveryAddress";
import routes from "../../../common/routes";
import Error from "src/core/common/components/elements/Error";
import Title from "src/core/common/components/modules/Title";
import useInventoryType from "src/core/inventories/useInventoryType";
import StateSelect from "src/core/authentication/components/StateSelect";
import DriversLicenseIDFields from "src/core/authentication/components/DriversLicenseIDFields";
import IdentityVerificationLogo from "src/integrations/identityVerification/components/IdentityVerificationLogo";
import SaveCheckoutInfo from "src/core/checkout/components/SaveCheckoutInfo";
import PersonalInfoSection from "src/core/user/components/PersonalInfoSection";

export default function ExpressOnBoardingModal({
  isOpen,
  closeModal = () => {},
  ...props
}) {
  const router = useRouter();
  const [, authApi] = useAuthentication();
  const [address] = useDeliveryAddress();
  const toggles = useFeatureToggles();

  function isAllowedToClose() {
    return Boolean(address) || !toggles.deliveriesOnly();
  }

  function close() {
    if (isAllowedToClose()) {
      closeModal();
    } else {
      router.push(routes.deliveryCheck);
    }
    authApi.resetErrors();
  }

  return (
    <Modal isOpen={isOpen} onRequestClose={close} replaceOnClose={props.replaceOnClose}>
      <PersonalInfoForm {...props} />
    </Modal>
  );
}

function PersonalInfoForm({
  onSuccess,
  builder,
  showSaveCheckoutInfo,
  initialValues,
  title,
  subtitle,
  submitLabel,
}) {
  const [marketingSource] = useMarketingSourceParam();
  const [authState] = useAuthentication();
  const toggles = useFeatureToggles();
  const site = useSite();
  const [deliveryType] = useDeliveryType();
  const [inventoryType] = useInventoryType();
  const saveCheckoutInfoByDefault = site.getOptions().saveCheckoutInfoByDefault({
    inventoryType,
    deliveryType: deliveryType ? deliveryType.code : null,
  });

  const requestEmailOnSignUp = toggles.requestEmailOnSignUp();
  const requestPhoneOnSignUp = toggles.requestPhoneOnSignUp();

  const _initialValues = useMemo(
    () => ({
      marketingEmailOptIn: requestEmailOnSignUp,
      marketingSmsOptIn: requestPhoneOnSignUp,
      marketingSource: marketingSource
        ? {value: marketingSource, label: marketingSource}
        : undefined,
      stateResidency: toggles.defaultStateResidency(),
      saveCheckoutInfo: toggles.isKiosk()
        ? false
        : !showSaveCheckoutInfo || saveCheckoutInfoByDefault,
      ...initialValues,
    }),
    [
      requestEmailOnSignUp,
      requestPhoneOnSignUp,
      marketingSource,
      toggles,
      showSaveCheckoutInfo,
      saveCheckoutInfoByDefault,
      initialValues,
    ]
  );

  const [dateOfBirthValue, setDateOfBirthValue] = useFormsCache(FormFields.DATE_OF_BIRTH);
  const [stateResidencyValue, setStateResidencyValue] = useFormsCache(
    FormFields.STATE_RESIDENCY
  );

  const onChangeDateOfBirth = curry((callback, value) => {
    setDateOfBirthValue(value);
    callback(value);
  });

  const [minimumAgeDate, age] = useAgeValidation();

  function onSubmit(formValues) {
    builder
      .setFirstName(formValues.firstName)
      .setLastName(formValues.lastName)
      .setDateOfBirth(formValues.dateOfBirth)
      .setPhoneNumber(formValues.phoneNumber)
      .setEmail(formValues.email)
      .setStateResidency(formValues.stateResidency)
      .setMarketingEmailOptIn(formValues.marketingEmailOptIn)
      .setMarketingSmsOptIn(formValues.marketingSmsOptIn)
      .setMarketingSource(get(formValues.marketingSource, "value", undefined))
      .setDriversLicenseIdNumber(formValues.driversLicenseIdNumber)
      .setDriversLicenseIdExpirationDate(formValues.driversLicenseIdExpirationDate);
    onSuccess(formValues);
  }

  function displayMarketingSources(saveCheckoutInfo) {
    return (
      toggles.allowMarketingSourceOnSignUp() &&
      (!showSaveCheckoutInfo || saveCheckoutInfo)
    );
  }
  const error = getErrorMessages({data: authState.error})[0];

  return (
    <Form
      onSubmit={onSubmit}
      validate={() => {}}
      initialValues={_initialValues}
      render={({handleSubmit, values}) => (
        <form onSubmit={handleSubmit} noValidate={true}>
          <FormContainer style={{paddingBottom: "16px"}}>
            <Title lineHeight={1} letterSpacing={1}>
              {title}
            </Title>
            {subtitle && <Subtitle>{subtitle}</Subtitle>}
            {error && <Error style={{textAlign: "center"}}>{error}</Error>}
            <CustomCheckoutPersonalInfoSection>
              <ContactInfoFormSection />

              {toggles.registrationWithDriversLicenseID() && <DriversLicenseIDFields />}

              {toggles.requiresDateOfBirthOnSignUp() && (
                <DatePicker
                  name={"dateOfBirth"}
                  placeholder={"Date of Birth*"}
                  initialValue={_initialValues.dateOfBirth || dateOfBirthValue}
                  maxDate={minimumAgeDate}
                  minDate={yearsAgo(150)}
                  validate={composeValidators(required, age)}
                  onChange={onChangeDateOfBirth}
                />
              )}

              {toggles.displayStateResidencyOnSignUp() && (
                <StateSelect
                  name={"stateResidency"}
                  defaultState={stateResidencyValue}
                  onChange={setStateResidencyValue}
                  placeholder={"Residency State"}
                  formatSelection={entry => `I am a resident of ${entry.label}`}
                  required={toggles.requiresStateResidencyOnSignUp()}
                />
              )}

              {displayMarketingSources(values.saveCheckoutInfo) && (
                <MarketingSourcesSelect />
              )}

              <SaveCheckoutInfoMask show={showSaveCheckoutInfo && !toggles.isKiosk()}>
                <SaveCheckoutInfo />
              </SaveCheckoutInfoMask>

              <MarketingOptInField
                requestEmail={requestEmailOnSignUp}
                requestPhone={requestPhoneOnSignUp}
              />
            </CustomCheckoutPersonalInfoSection>
            <PrimaryLoadingButton
              loading={authState.loading}
              label={submitLabel || "Checkout"}
              data-cy="expressOnBoardingCheckoutButton"
              type="submit"
            />
            {!toggles.isKiosk() && (
              <FormFooter alignment={"center"}>
                <GoToLogin replace={true} />
              </FormFooter>
            )}
            <IdentityVerificationLogo />
          </FormContainer>
        </form>
      )}
    />
  );
}

const Subtitle = styled.span`
  font-size: 16px;
  color: #333333;
`;

const CustomCheckoutPersonalInfoSection = styled(PersonalInfoSection)`
  ${TitleLabel} {
    text-align: left;
    font-size: ${({theme}) => theme.v2.typography.sizing.m.lg};
    margin-bottom: ${({theme}) => theme.v2.spacing(2)} !important;
  }
`;
const SaveCheckoutInfoMask = styled.div`
  ${({show}) => (!show ? "display: none;" : "")}
`;

const FormFooter = styled(FormText)`
  margin-top: ${({theme}) => theme.v2.spacing(4)};
`;
